import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import LandingStep from './LandingStep';
import PatientStatusStep from './PatientStatusStep';
import AppointmentTypeStep from './AppointmentTypeStep';
import AppointmentTypeZipStep from './AppointmentTypeZipStep';
import ProviderSelectionStep from './ProviderSelectionStep';
import PrequalificationStep from './PrequalificationStep';
import PatientInfoStep from './PatientInfoStep';
import ConfirmationStep from './ConfirmationStep';
import SuccessStep from './SuccessStep';
import ExistingAppointmentsStep from './ExistingAppointmentsStep';
import { PatientInfoFormData } from './PatientInfoStep';
import ManageAppointmentStep from './ManageAppointmentStep';
import EditAppointmentRouter from './EditAppointmentRouter';
import TherapyPreferenceStep from './TherapyPreferenceStep';
import MedicaidQuestionStep from './MedicaidQuestionStep';
import { getOpenSlots, getProviderSpecificOpenSlots } from '../../utils/apis';
import { useQuery } from 'react-query';

// Type definitions for better type safety
interface Provider {
  providerId: string;
  displayName?: string;
  schedulingName?: string;
  firstName: string;
  lastName: string;
  specialty?: string;
  departmentId: string;
  departmentName: string;
  departmentZip?: string;
  departmentAddress?: string;
  departmentCity?: string;
  departmentState?: string;
  availableSlots: AppointmentSlot[];
}

interface AppointmentSlot {
  startTime: string;
  endTime: string;
  duration?: number;
  appointmentId: string;
  appointmentName?: string;
  appointmentTypeId: string;
}

type SelectedAppointment = {
  provider: any;
  slot: any;
};

interface ExistingAppointment {
  appointmentid: string;
  patientid: string;
  providerid: string;
  departmentid: string;
  appointmenttypeid: string;
  scheduleddatetime: string;
  date: string;
  starttime: string;
  duration: number;
  appointmenttype: string;
  appointmentstatus: string;
  athenaAppointmentId?: string;
  athenaPatientId?: string;
  athenaProviderId?: string;
  athenaDepartmentId?: string;
  appointmentTypeId?: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  reason?: string;
  reasonType?: string;
  status?: string;
  providerName?: string;
  departmentName?: string;
}

interface PatientData {
  athenaPatientId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  sex?: string;
  address?: string;
  zipCode?: string;
  existingPatient?: boolean;
  upcomingAppointments?: boolean;
}

// Enum for steps
enum Step {
  TherapyPreference,
  Landing,
  PatientStatus,
  ExistingAppointments,
  RescheduleAppointmentTypeZip,
  RescheduleProviderSelection,
  RescheduleExisting,
  AppointmentType,
  AppointmentTypeZip,
  ProviderSelection,
  Prequalification,
  MedicaidQuestion,
  PatientInfo,
  Confirmation,
  ManageAppointment,
  EditAppointment,
}

const AxisSchedulingRouter: React.FC = () => {
  const { providerIdentifier: paramsProviderIdentifier } = useParams<{ providerIdentifier?: string }>();
  
  // Extract appointment type and provider identifier from URL path
  const getAppointmentTypeAndProviderFromPath = () => {
    const path = window.location.pathname;
    
    // Check for Spravato routes
    const spravatoProviderMatch = path.match(/\/spravato\/provider\/([^/]+)/);
    if (spravatoProviderMatch) {
      return { appointmentType: 'spravato', providerIdentifier: spravatoProviderMatch[1] };
    }
    
    const spravatoMatch = path.match(/\/spravato\/?$/);
    if (spravatoMatch) {
      return { appointmentType: 'spravato', providerIdentifier: null };
    }
    
    // Check for regular provider routes
    const providerMatch = path.match(/\/provider\/([^/]+)/);
    if (providerMatch) {
      return { appointmentType: 'regular', providerIdentifier: providerMatch[1] };
    }
    
    return { appointmentType: 'regular', providerIdentifier: null };
  };
  
  const { appointmentType: urlAppointmentType, providerIdentifier: pathProviderIdentifier } = getAppointmentTypeAndProviderFromPath();
  const providerIdentifier = paramsProviderIdentifier || pathProviderIdentifier;
  
  // Providers that don't accept new patients for regular provider route
  const restrictedProviders = [
    'Dr_Ross_VanAllen_DNP',
    'Dr_Jamie_Teunis_DNP', 
    'Frances_Rhodes_NP',
    'Dr_Roderick_OBrienMD'
  ];
  const isRestrictedProvider = urlAppointmentType === 'regular' && providerIdentifier && restrictedProviders.includes(providerIdentifier);
  
  // URL parsing logic for appointment type and provider identification
  
  const [step, setStep] = useState<Step>(Step.TherapyPreference);
  // Placeholder for collected data
  const [appointmentType, setAppointmentType] = useState<'telehealth' | 'inperson' | 'spravato' | null>(null);
  const [zip, setZip] = useState('');
  const [radiusMiles, setRadiusMiles] = useState<number>(25);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [slot, setSlot] = useState<AppointmentSlot | null>(null);
  const [selectedAppointments, setSelectedAppointments] = useState<SelectedAppointment[]>([]);
  const [patientInfo, setPatientInfo] = useState<PatientInfoFormData | null>(null);
  const [prequalificationAnswers, setPrequalificationAnswers] = useState<Record<number, string>>({});
  const [hasMedicaid, setHasMedicaid] = useState<boolean | null>(null);
  const [success, setSuccess] = useState(false);
  const [editAppointment, setEditAppointment] = useState<ExistingAppointment | null>(null);
  const [patientStatus, setPatientStatus] = useState<'existing' | 'new' | null>(null);
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [reschedulingAppointment, setReschedulingAppointment] = useState<ExistingAppointment | null>(null);
  const [initialUserChoice, setInitialUserChoice] = useState<'new' | 'existing' | null>(null);
  const [currentStartDate, setCurrentStartDate] = useState<Date>(() => {
    // Start with today + 2 days
    const today = new Date();
    today.setDate(today.getDate() + 2);
    return today;
  });

  // Reschedule flow state
  const [rescheduleAppointmentType, setRescheduleAppointmentType] = useState<'telehealth' | 'inperson' | 'spravato' | null>(null);
  const [rescheduleZip, setRescheduleZip] = useState('');
  const [rescheduleRadiusMiles, setRescheduleRadiusMiles] = useState<number>(25);
  const [rescheduleCurrentStartDate, setRescheduleCurrentStartDate] = useState<Date>(() => {
    // Start with today + 2 days
    const today = new Date();
    today.setDate(today.getDate() + 2);
    return today;
  });

  const isSpravatoFlow = urlAppointmentType === 'spravato';
  const canBulkBookSpravato = isSpravatoFlow && !!patientData?.athenaPatientId;

  // Map Spravato provider identifiers to zipcodes
  const getSpravatoZipcode = (providerId: string | null): string | null => {
    if (!providerId) return null;
    
    // Check if we're in stage or local environment
    const isStageOrLocal = window.location.hostname.includes('localhost') || 
                          window.location.hostname.includes('dev') || 
                          window.location.hostname.includes('stage');
    
    // For stage/local environments, use test zipcodes
    if (isStageOrLocal) {
      const stageZipcodes = ['07932', '07960', '04915', '02472'];
      // Use first zipcode as default, or you can map them to specific providers
      // For now, using first zipcode for all providers in stage/local
      return stageZipcodes[1];
    }
    
    // Production zipcode mappings
    const mapping: { [key: string]: string } = {
      'Louisville_Spravato': '80027',
      'Aurora_Spravato': '80012',
      'Westminster_Spravato': '80031',
    };
    return mapping[providerId] || null;
  };

  // Auto-set appointment type, zip, and radius for Spravato routes
  React.useEffect(() => {
    if (urlAppointmentType === 'spravato') {
      setAppointmentType('spravato');
      // Auto-set zipcode and radius based on provider identifier
      const spravatoZip = getSpravatoZipcode(providerIdentifier);
      if (spravatoZip) {
        setZip(spravatoZip);
        // Check if we're in stage or local environment
        const isStageOrLocal = window.location.hostname.includes('localhost') || 
                              window.location.hostname.includes('dev') || 
                              window.location.hostname.includes('stage');
        // Use 1000 miles for stage/local, 25 miles for production
        const radius = isStageOrLocal ? 1000 : 25;
        setRadiusMiles(radius);
        // Also set for reschedule flow
        setRescheduleZip(spravatoZip);
        setRescheduleRadiusMiles(radius);
      }
    }
  }, [urlAppointmentType, providerIdentifier]);

  // Step navigation handlers
  const goBack = () => setStep((prev) => (prev > 0 ? (prev - 1) as Step : prev));

  // Always call hooks at the top level
  const {
    data: rawOpenSlots = [],
    isLoading: providersLoading,
    error: providersError,
  } = useQuery(
    ['openSlots', appointmentType, zip, radiusMiles, patientStatus, providerIdentifier, currentStartDate],
    () => {
      const startDate = currentStartDate.toISOString().split('T')[0];
      
      const requestData: any = {
        reasonType: patientStatus === 'existing' ? 'existing' : 'new',
        reason: appointmentType === 'spravato' ? 'Spravato' : (appointmentType === 'telehealth' ? 'Telehealth' : 'In-Person'),
        startDate: startDate
      };

      // For Spravato, add endDate for 2 months (60 days)
      if (appointmentType === 'spravato') {
        const endDate = new Date(currentStartDate);
        endDate.setDate(endDate.getDate() + 60);
        requestData.endDate = endDate.toISOString().split('T')[0];
      }

      // Add location parameters for in-person appointments (including Spravato)
      if ((appointmentType === 'inperson' || appointmentType === 'spravato') && zip) {
        requestData.zipCode = zip;
        requestData.radiusMiles = radiusMiles;
      }

      if (providerIdentifier) {
        return getProviderSpecificOpenSlots(providerIdentifier, requestData);
      }
      return getOpenSlots(requestData);
    },
    {
      enabled: step === Step.ProviderSelection && !!patientStatus && !isRestrictedProvider && (
        appointmentType === 'telehealth' || 
        (appointmentType === 'inperson' && !!zip && radiusMiles > 0) ||
        (appointmentType === 'spravato' && !!zip && radiusMiles > 0)
      ),
      onError: () => {
        // Error handling is managed by react-query and error boundaries
      }
    }
  );

  const providersErrorMessage = React.useMemo(() => {
    if (!providersError) return null;
    if (providersError instanceof Error) return providersError.message;
    const maybeMessage = (providersError as any)?.message;
    if (typeof maybeMessage === 'string') return maybeMessage;
    return 'We were unable to load providers. Please try again.';
  }, [providersError]);

  // Reschedule open slots query
  const {
    data: rawRescheduleOpenSlots = [],
    isLoading: rescheduleProvidersLoading,
    error: rescheduleProvidersError,
  } = useQuery(
    ['rescheduleOpenSlots', rescheduleAppointmentType, rescheduleZip, rescheduleRadiusMiles, patientStatus, providerIdentifier, rescheduleCurrentStartDate],
    () => {
      const startDate = rescheduleCurrentStartDate.toISOString().split('T')[0];
      const requestData: any = {
        reasonType: patientStatus === 'existing' ? 'existing' : 'new',
        reason: rescheduleAppointmentType === 'spravato' ? 'Spravato' : (rescheduleAppointmentType === 'telehealth' ? 'Telehealth' : 'In-Person'),
        startDate: startDate
      };

      // Add location parameters for in-person appointments (including Spravato)
      if ((rescheduleAppointmentType === 'inperson' || rescheduleAppointmentType === 'spravato') && rescheduleZip) {
        requestData.zipCode = rescheduleZip;
        requestData.radiusMiles = rescheduleRadiusMiles;
      }

      if (providerIdentifier) {
        return getProviderSpecificOpenSlots(providerIdentifier, requestData);
      }
      return getOpenSlots(requestData);
    },
    {
      enabled: step === Step.RescheduleProviderSelection && !!patientStatus && !isRestrictedProvider && (
        rescheduleAppointmentType === 'telehealth' || 
        (rescheduleAppointmentType === 'inperson' && !!rescheduleZip && rescheduleRadiusMiles > 0) ||
        (rescheduleAppointmentType === 'spravato' && !!rescheduleZip && rescheduleRadiusMiles > 0)
      ),
      onError: () => {
        // Error handling is managed by react-query and error boundaries
      }
    }
  );

  const rescheduleProvidersErrorMessage = React.useMemo(() => {
    if (!rescheduleProvidersError) return null;
    if (rescheduleProvidersError instanceof Error) return rescheduleProvidersError.message;
    const maybeMessage = (rescheduleProvidersError as any)?.message;
    if (typeof maybeMessage === 'string') return maybeMessage;
    return 'We were unable to load providers. Please try again.';
  }, [rescheduleProvidersError]);

  // Filter providers based on providerIdentifier if present
  const openSlots = React.useMemo(() => {
    // If it's a restricted provider, return empty array (no API call made)
    if (isRestrictedProvider) {
      return [];
    }
    
    if (!providerIdentifier || !rawOpenSlots?.length) {
      return rawOpenSlots;
    }

    // Filter providers that match the providerIdentifier
    const filteredProviders = rawOpenSlots.filter((provider: any) => {
      // Check if provider matches by scheduling name (like "Meredith_Grey")
      const schedulingName = provider.schedulingName || `${provider.firstName}_${provider.lastName}`;
      const providerId = provider.providerId;
      
      // Match by scheduling name (case-insensitive)
      const nameMatch = schedulingName.toLowerCase() === providerIdentifier.toLowerCase();
      // Match by provider ID if it's numeric
      const idMatch = providerId === providerIdentifier;
      
      return nameMatch || idMatch;
    });

    return filteredProviders;
  }, [rawOpenSlots, providerIdentifier, isRestrictedProvider]);

  // Filter reschedule providers based on providerIdentifier if present
  const rescheduleOpenSlots = React.useMemo(() => {
    // If it's a restricted provider, return empty array (no API call made)
    if (isRestrictedProvider) {
      return [];
    }
    
    if (!providerIdentifier || !rawRescheduleOpenSlots?.length) {
      return rawRescheduleOpenSlots;
    }

    // Filter providers that match the providerIdentifier
    const filteredProviders = rawRescheduleOpenSlots.filter((provider: any) => {
      // Check if provider matches by scheduling name (like "Meredith_Grey")
      const schedulingName = provider.schedulingName || `${provider.firstName}_${provider.lastName}`;
      const providerId = provider.providerId;
      
      // Match by scheduling name (case-insensitive)
      const nameMatch = schedulingName.toLowerCase() === providerIdentifier.toLowerCase();
      // Match by provider ID if it's numeric
      const idMatch = providerId === providerIdentifier;
      
      return nameMatch || idMatch;
    });

    return filteredProviders;
  }, [rawRescheduleOpenSlots, providerIdentifier, isRestrictedProvider]);

  // Step render logic
  switch (step) {
    case Step.TherapyPreference:
      return (
        <TherapyPreferenceStep
          onProceed={() => {
            setSelectedAppointments([]);
            setProvider(null);
            setSlot(null);
            setStep(Step.Landing);
          }}
        />
      );
    case Step.Landing:
      if (success) return <SuccessStep onRestart={() => setSuccess(false)} />;
      return <LandingStep onSelect={(type) => {
        setInitialUserChoice(type);
        setSelectedAppointments([]);
        setProvider(null);
        setSlot(null);
        setStep(Step.PatientStatus);
      }} />;
    case Step.PatientStatus:
      return (
        <PatientStatusStep
          isSpravatoFlow={urlAppointmentType === 'spravato'}
          onProceed={(status: 'existing' | 'new', data: any) => {
            setPatientStatus(status);
            setPatientData(data);
            setSelectedAppointments([]);
            setProvider(null);
            setSlot(null);
            
            // Flow logic based on initial user choice and backend response
            if (initialUserChoice === 'existing') {
              // If backend confirms existing or has upcoming appointments, show existing appointments; otherwise go to selection
              if (status === 'existing' || data?.upcomingAppointments === true) {
                setStep(Step.ExistingAppointments);
              } else {
                // For existing patients without appointments
                if (urlAppointmentType === 'spravato') {
                  // Skip zipcode step for Spravato - go directly to provider selection
                  setStep(Step.ProviderSelection);
                } else {
                  setStep(Step.AppointmentTypeZip);
                }
              }
            } else if (initialUserChoice === 'new') {
              // For new patients
              if (urlAppointmentType === 'spravato') {
                // Skip zipcode step for Spravato - go directly to provider selection
                setStep(Step.ProviderSelection);
              } else {
                setStep(Step.AppointmentTypeZip);
              }
            } else {
              // Fallback - should not happen
              if (urlAppointmentType === 'spravato') {
                setStep(Step.ProviderSelection);
              } else {
                setStep(Step.AppointmentTypeZip);
              }
            }
          }}
          onBack={() => setStep(Step.Landing)}
        />
      );
    case Step.ExistingAppointments:
      return patientData?.athenaPatientId ? (
        <ExistingAppointmentsStep
          onBack={() => setStep(Step.PatientStatus)}
          onReschedule={(appointment) => {
            setReschedulingAppointment(appointment);
            setSelectedAppointments([]);
            setProvider(null);
            setSlot(null);
            // For Spravato flow, skip zipcode step and go directly to provider selection
            if (urlAppointmentType === 'spravato') {
              setRescheduleAppointmentType('spravato');
              setStep(Step.RescheduleProviderSelection);
            } else {
              setStep(Step.RescheduleAppointmentTypeZip);
            }
          }}
          onNewAppointment={() => {
            // For Spravato, skip zipcode step and go directly to provider selection
            if (urlAppointmentType === 'spravato') {
              setStep(Step.ProviderSelection);
            } else {
              setStep(Step.AppointmentTypeZip);
            }
          }}
          patientId={patientData.athenaPatientId}
          urlAppointmentType={urlAppointmentType as 'spravato' | 'regular'}
        />
      ) : null;
    case Step.RescheduleAppointmentTypeZip:
      return (
        <AppointmentTypeZipStep
          selectedType={rescheduleAppointmentType === 'spravato' ? 'inperson' : (rescheduleAppointmentType || 'telehealth')}
          onSelectType={(type) => setRescheduleAppointmentType(type)}
          zip={rescheduleZip}
          onZipChange={setRescheduleZip}
          radiusMiles={rescheduleRadiusMiles}
          onRadiusMilesChange={setRescheduleRadiusMiles}
          onProceed={() => setStep(Step.RescheduleProviderSelection)}
          onBack={() => setStep(Step.ExistingAppointments)}
          isSpravato={urlAppointmentType === 'spravato'}
        />
      );
    case Step.RescheduleProviderSelection:
      return (
        <ProviderSelectionStep
          onBack={() => {
            // For Spravato, go back to ExistingAppointments since we skipped RescheduleAppointmentTypeZip
            if (urlAppointmentType === 'spravato') {
              setStep(Step.ExistingAppointments);
            } else {
              setStep(Step.RescheduleAppointmentTypeZip);
            }
          }}
          onSelect={(prov: any, slot: any) => {
            setProvider(prov);
            setSlot(slot);
            setStep(Step.RescheduleExisting);
          }}
          onNewSearch={() => setStep(Step.ExistingAppointments)}
          providers={rescheduleOpenSlots}
          isLoading={rescheduleProvidersLoading}
          error={rescheduleProvidersErrorMessage}
          reasonType={patientStatus === 'existing' ? 'existing' : 'new'}
          currentStartDate={rescheduleCurrentStartDate}
          onNextWeek={() => {
            const newDate = new Date(rescheduleCurrentStartDate);
            newDate.setDate(newDate.getDate() + 14);
            setRescheduleCurrentStartDate(newDate);
          }}
          onPreviousWeek={() => {
            const newDate = new Date(rescheduleCurrentStartDate);
            newDate.setDate(newDate.getDate() - 14);
            setRescheduleCurrentStartDate(newDate);
          }}
        />
      );
    case Step.RescheduleExisting:
      return reschedulingAppointment && patientData?.athenaPatientId && provider && slot ? (
        <ConfirmationStep
          onBack={() => setStep(Step.RescheduleProviderSelection)}
          onSuccess={() => {
            setReschedulingAppointment(null);
            setProvider(null);
            setSlot(null);
            setSelectedAppointments([]);
            setStep(Step.Landing);
          }}
          provider={provider}
          slot={slot}
          appointmentType={rescheduleAppointmentType === 'spravato' ? 'spravato' : (rescheduleAppointmentType === 'telehealth' ? 'telehealth' : 'inperson')}
          patientInfo={null}
          patientStatus="existing"
          patientData={patientData}
          reschedulingAppointment={reschedulingAppointment}
        />
      ) : null;
    case Step.AppointmentType:
      return <AppointmentTypeStep onSelect={(type) => { setAppointmentType(type); setStep(Step.AppointmentTypeZip); }} onBack={() => setStep(Step.Landing)} />;
    case Step.AppointmentTypeZip:
      return (
        <AppointmentTypeZipStep
          selectedType={appointmentType === 'spravato' ? 'inperson' : (appointmentType ?? 'telehealth')}
          onSelectType={setAppointmentType}
          zip={zip}
          onZipChange={setZip}
          radiusMiles={radiusMiles}
          onRadiusMilesChange={setRadiusMiles}
          isSpravato={urlAppointmentType === 'spravato'}
          onProceed={() => {
            // For Spravato, ensure appointment type is set to spravato
            if (urlAppointmentType === 'spravato') {
              setAppointmentType('spravato');
            } else if (!appointmentType) {
              // Default to telehealth if user doesn't toggle cards explicitly
              setAppointmentType('telehealth');
            }
            setSelectedAppointments([]);
            setProvider(null);
            setSlot(null);
            setStep(Step.ProviderSelection);
          }}
          onBack={() => setStep(Step.PatientStatus)}
        />
      );
    case Step.ProviderSelection:
      return appointmentType ? (
        <ProviderSelectionStep
          onBack={() => {
            setSelectedAppointments([]);
            setProvider(null);
            setSlot(null);
            // For Spravato, go back to PatientStatus since we skipped AppointmentTypeZip
            if (urlAppointmentType === 'spravato') {
              setStep(Step.PatientStatus);
            } else {
              setStep(Step.AppointmentTypeZip);
            }
          }}
          onSelect={(prov: any, slot: any) => { 
            setProvider(prov); 
            setSlot(slot); 
            setSelectedAppointments([{ provider: prov, slot }]);
            // For Spravato, go to Medicaid question; for regular, go to prequalification
            if (isSpravatoFlow) {
              setStep(Step.MedicaidQuestion);
            } else {
              setStep(Step.Prequalification);
            }
          }}
          onNewSearch={() => {
            setSelectedAppointments([]);
            setProvider(null);
            setSlot(null);
            setStep(Step.Landing);
          }}
          providers={openSlots}
          isLoading={providersLoading}
          error={providersErrorMessage}
          reasonType={patientStatus}
          isRestrictedProvider={!!isRestrictedProvider}
          currentStartDate={currentStartDate}
          multiSelectEnabled={canBulkBookSpravato}
          selectedBulkAppointments={selectedAppointments}
          onBulkSelectionChange={(items) => setSelectedAppointments(items)}
          onBulkProceed={(items) => {
            if (!items || items.length === 0) {
              return;
            }
            setSelectedAppointments(items);
            setProvider(items[0].provider as Provider);
            setSlot(items[0].slot as AppointmentSlot);
            // For Spravato bulk booking, go to Medicaid question
            if (isSpravatoFlow) {
              setStep(Step.MedicaidQuestion);
            } else {
              setStep(Step.PatientInfo);
            }
          }}
          onNextWeek={() => {
            const newDate = new Date(currentStartDate);
            newDate.setDate(newDate.getDate() + 14);
            setCurrentStartDate(newDate);
          }}
          onPreviousWeek={() => {
            const newDate = new Date(currentStartDate);
            newDate.setDate(newDate.getDate() - 14);
            setCurrentStartDate(newDate);
          }}
        />
      ) : null;
    case Step.Prequalification:
      return (
        <PrequalificationStep 
          onBack={goBack} 
          onProceed={() => setStep(Step.PatientInfo)}
          initialAnswers={prequalificationAnswers}
          onAnswersChange={setPrequalificationAnswers}
        />
      );
    case Step.MedicaidQuestion:
      return (
        <MedicaidQuestionStep
          onBack={() => setStep(Step.ProviderSelection)}
          onProceed={(medicaid: boolean) => {
            setHasMedicaid(medicaid);
            // Set patientInfo with insurance information for Spravato
            // If Medicaid selected, use the Medicaid option; if No, set to null/empty
            const insuranceValue = medicaid 
              ? 'Colorado Medicaid (with the exception of Denver Health Medicaid)'
              : null;
            setPatientInfo({
              insurance: insuranceValue || '',
              insuranceId: medicaid ? 'On file' : '',
              reasonForVisit: 'Spravato treatment',
              howDidYouHear: '',
              // Add required fields with empty/default values
              firstName: patientData?.firstName || '',
              lastName: patientData?.lastName || '',
              email: patientData?.email || '',
              dateOfBirth: patientData?.dateOfBirth ? {
                day: '',
                month: '',
                year: ''
              } : { day: '', month: '', year: '' },
              sex: '',
              phoneType: 'Mobile',
              phoneNumber: patientData?.phone || '',
              allowTextMessages: true,
              address: '',
              zipCode: patientData?.zipCode || ''
            } as PatientInfoFormData);
            setStep(Step.Confirmation);
          }}
        />
      );
    case Step.PatientInfo:
      return (
        <PatientInfoStep 
          onBack={goBack} 
          onProceed={(formData) => { setPatientInfo(formData); setStep(Step.Confirmation); }}
          initialData={patientInfo}
          patientData={patientData}
          isSpravato={appointmentType === 'spravato'}
        />
      );
    case Step.Confirmation: {
      const hasBulkSelection = selectedAppointments.length > 0;
      const confirmationProvider = provider || (hasBulkSelection ? selectedAppointments[0]?.provider : null);
      const confirmationSlot = slot || (hasBulkSelection ? selectedAppointments[0]?.slot : null);

      if (!confirmationProvider || !confirmationSlot) {
        return null;
      }

      return (
        <ConfirmationStep
          onBack={() => {
            // For Spravato, go back to MedicaidQuestionStep; for regular, use default goBack
            if (isSpravatoFlow) {
              setStep(Step.MedicaidQuestion);
            } else {
              goBack();
            }
          }}
          provider={confirmationProvider}
          slot={confirmationSlot}
          appointmentType={appointmentType}
          patientInfo={patientInfo}
          patientStatus={patientStatus || undefined}
          patientData={patientData}
          reschedulingAppointment={reschedulingAppointment}
          selectedAppointments={selectedAppointments}
          onSuccess={() => {
            setSuccess(true);
            setStep(Step.Landing);
            setAppointmentType(null);
            setProvider(null);
            setSlot(null);
            setSelectedAppointments([]);
            setPatientInfo(null);
            setPrequalificationAnswers({});
            setHasMedicaid(null);
            setZip('');
            setPatientStatus(null);
            setPatientData(null);
            setReschedulingAppointment(null);
            setInitialUserChoice(null);
          }}
        />
      );
    }
    case Step.ManageAppointment:
      return <ManageAppointmentStep onBack={() => setStep(Step.Landing)} onDone={() => setStep(Step.Landing)} onEdit={(appt) => { setEditAppointment(appt); setStep(Step.EditAppointment); }} />;
    case Step.EditAppointment:
      return <EditAppointmentRouter appointment={editAppointment} onDone={() => setStep(Step.Landing)} />;
    default:
      return null;
  }
};

export default AxisSchedulingRouter; 